import { useCallback, useEffect, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errMsg, img } from "@/lib/api";
import { ImageCropper } from "./ImageCropper";

export const PageHead = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
    <div><h1 className="text-2xl lg:text-3xl font-bold" data-testid="admin-page-title">{title}</h1>{sub && <p className="text-sm text-[#A0A0A0] mt-1">{sub}</p>}</div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, hint, testid }) => (
  <div className="card p-5" data-testid={testid}><div className="text-[11px] uppercase tracking-wider text-[#A0A0A0] font-semibold">{label}</div><div className="text-2xl font-bold font-display mt-2">{value}</div>{hint && <div className="text-xs text-[#6b7079] mt-1">{hint}</div>}</div>
);

export const Empty = ({ text = "Nenhum registro encontrado." }) => <div className="py-16 text-center text-sm text-[#A0A0A0]" data-testid="empty-state">{text}</div>;
export const Loading = () => <div className="py-16 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-[#00C8FF]" /></div>;

export const Table = ({ children }) => <div className="card overflow-x-auto"><table className="tbl">{children}</table></div>;

const TONES = { ok: "bg-emerald-500/10 text-emerald-400", warn: "bg-yellow-500/10 text-yellow-400", bad: "bg-red-500/10 text-red-400", info: "bg-[#008CFF]/10 text-[#00C8FF]", mute: "bg-[#282D37] text-[#A0A0A0]" };
const TONE_OF = { aprovado: "ok", entregue: "ok", recebido: "ok", pago: "ok", disponivel: "ok", pronto: "ok", pendente: "warn", aguardando_pagamento: "warn", em_processamento: "warn", manutencao: "warn",
  recusado: "bad", cancelado: "bad", reembolsado: "bad", contestado: "bad", parada: "bad", excluido: "bad" };
export const Chip = ({ s, label }) => <span className={`chip whitespace-nowrap ${TONES[TONE_OF[s] || "info"]}`}>{label || s}</span>;

export function Modal({ open, onClose, title, children, wide }) {
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className={`bg-[#0B0D11] border-[#282D37] text-white max-h-[90vh] overflow-y-auto ${wide ? "sm:max-w-4xl" : "sm:max-w-xl"}`}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>{children}
      </DialogContent>
    </Dialog>
  );
}

export function Confirm({ open, onClose, onConfirm, title, text, children }) {
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent className="bg-[#0B0D11] border-[#282D37] text-white">
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription className="text-[#A0A0A0]">{text}</AlertDialogDescription></AlertDialogHeader>
        {children}
        <AlertDialogFooter><AlertDialogCancel className="bg-transparent border-[#282D37]" data-testid="confirm-cancel">Cancelar</AlertDialogCancel><AlertDialogAction className="bg-[#008CFF] hover:bg-[#004DCC]" onClick={onConfirm} data-testid="confirm-ok">Confirmar</AlertDialogAction></AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function ImageInput({ value, onChange, multiple, testid, crop }) {
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState({ files: [], i: 0, out: [] });
  const list = multiple ? value || [] : value ? [value] : [];
  const up = async (files) => {
    setBusy(true);
    const urls = [];
    for (const f of files) {
      try { const fd = new FormData(); fd.append("file", f, f.name || "foto.jpg"); urls.push((await api.post("/uploads?public=true", fd)).data.url); } catch (e) { toast.error(errMsg(e)); }
    }
    setBusy(false);
    if (urls.length) onChange(multiple ? [...list, ...urls] : urls[0]);
  };
  const pick = (files) => {
    const imgs = files.filter((f) => f.type.startsWith("image/"));
    if (!crop) return up(imgs);
    if (imgs.length) setQueue({ files: imgs, i: 0, out: [] });
    return undefined;
  };
  const cropped = (blob) => {
    const { files, i } = queue;
    const out = blob ? [...queue.out, new File([blob], (files[i].name || "foto").replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" })] : queue.out;
    if (i + 1 < files.length) { setQueue({ files, i: i + 1, out }); return; }
    setQueue({ files: [], i: 0, out: [] });
    if (out.length) up(out);
  };
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {list.map((u, k) => (
          <div key={k} className="relative h-20 w-20 rounded-lg overflow-hidden border border-[#282D37]"><img src={img(u)} alt="" className="h-full w-full object-cover" />
            <button type="button" aria-label="Remover imagem" onClick={() => onChange(multiple ? list.filter((_, i) => i !== k) : "")} className="absolute top-1 right-1 bg-black/70 rounded-full p-0.5"><X className="h-3 w-3" /></button></div>
        ))}
        <label className="h-20 w-20 rounded-lg border border-dashed border-[#282D37] grid place-items-center cursor-pointer hover:border-[#008CFF]">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-[#A0A0A0]" />}
          <input type="file" accept="image/*" multiple={multiple} className="hidden" onChange={(e) => { pick([...e.target.files]); e.target.value = ""; }} data-testid={testid} />
        </label>
      </div>
      {crop && <p className="text-[11px] text-[#A0A0A0] mt-1">Padrão {crop[0]}×{crop[1]} px — você poderá cortar cada foto antes de inserir.</p>}
      {crop && <ImageCropper file={queue.files[queue.i]} index={queue.i} total={queue.files.length} w={crop[0]} h={crop[1]} onDone={cropped} />}
      <input className="input mt-2 text-xs" placeholder="ou cole a URL da imagem" onKeyDown={(e) => { if (e.key === "Enter" && e.target.value) { e.preventDefault(); onChange(multiple ? [...list, e.target.value] : e.target.value); e.target.value = ""; } }} />
    </div>
  );
}

export function Field({ f, value, onChange }) {
  const id = `fld-${f.name}`;
  const common = { id, "data-testid": `field-${f.name}` };
  let input;
  if (f.type === "textarea") input = <textarea {...common} className="input min-h-24" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  else if (f.type === "select") input = <select {...common} className="input" value={value ?? ""} onChange={(e) => onChange(e.target.value)}><option value="">Selecione</option>{f.options.map((o) => <option key={o[0]} value={o[0]}>{o[1]}</option>)}</select>;
  else if (f.type === "switch") return <label className="flex items-center gap-3 text-sm cursor-pointer"><input {...common} type="checkbox" className="h-4 w-4 accent-[#008CFF]" checked={!!value} onChange={(e) => onChange(e.target.checked)} />{f.label}</label>;
  else if (f.type === "image") input = <ImageInput value={value} onChange={onChange} testid={`field-${f.name}`} />;
  else if (f.type === "tags") input = <input {...common} className="input" value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((x) => x.trim()).filter(Boolean))} placeholder="separe por vírgula" />;
  else input = <input {...common} className="input" type={f.type || "text"} step="any" value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
  return <div className={f.full ? "sm:col-span-2" : ""}><label className="label" htmlFor={id}>{f.label}</label>{input}</div>;
}

export function useFetch(url, params) {
  const [data, setData] = useState(null);
  const key = JSON.stringify(params || {});
  const reload = useCallback(() => api.get(url, { params }).then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData((x) => x || []); }), [url, key]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [reload]);
  return [data, reload, setData];
}

export function downloadFile(url, name) {
  api.get(url, { responseType: "blob" }).then((r) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = name;
    a.click();
  }).catch((e) => toast.error(errMsg(e)));
}
